import { ecommerceApi as ecommerceClient, accountingApi } from '../config/apiClient';
import { ECOMMERCE_API_URL } from '../config/api';

const API_BASE_URL = `${ECOMMERCE_API_URL}/api/ecommerce`;

// Accounting API URL (Inventory için)
const ACCOUNTING_API_URL = process.env.REACT_APP_ACCOUNTINGAPI_URL || 'http://localhost:5237';
const INVENTORY_API_BASE_URL = `${ACCOUNTING_API_URL}/api/Inventory`;

console.log('🔧 [API CONFIG] E-ticaret API URL:', API_BASE_URL);
console.log('🔧 [API CONFIG] Inventory API URL:', INVENTORY_API_BASE_URL);

type CacheEntry<T> = {
  expiresAt: number;
  value: T;
};

export interface TrustStripItem {
  value?: string;
  label?: string;
}

export interface TrustStripSettings {
  items?: TrustStripItem[];
  backgroundColor?: string;
  textColor?: string;
  mutedColor?: string;
  style?: 'inline' | 'columns';
}

export interface GeneralConfigData {
  id?: string;
  companyId?: string;
  siteName?: string;
  homePageModuleStyle?: string;
  faviconUrl?: string;
  /** Paylaşım / OG görseli (og:image). Boşsa storefront header logosuna düşer. */
  metaImage?: string;
  metaTitle?: string;
  metaDescription?: string;
  metaKeywords?: string;
  layoutType?: string;
  containerMaxWidth?: number;
  homePageExcludeFromGrid?: boolean;
  whatsAppEnabled?: boolean;
  whatsAppPhone?: string;
  whatsAppMessage?: string;
  /** Site geneli marka rengi (vitrin --store-accent). */
  primaryColor?: string;
  /** Marka rengi hover tonu. */
  secondaryColor?: string;
  /** Site geneli yazı tipi (CSS font-family). */
  fontFamily?: string;
  showTrustStrip?: boolean;
  trustStripSettingsJson?: string;
}

export const ECOMMERCE_BOXED_LAYOUT_TYPE = 'boxed';
export const ECOMMERCE_DEFAULT_CONTAINER_MAX_WIDTH = 1440;

const runtimeCache = new Map<string, CacheEntry<any>>();

const getCached = <T>(key: string): T | null => {
  const hit = runtimeCache.get(key);
  if (!hit) return null;
  if (Date.now() > hit.expiresAt) {
    runtimeCache.delete(key);
    return null;
  }
  return hit.value as T;
};

const setCached = <T>(key: string, value: T, ttlMs: number) => {
  runtimeCache.set(key, {
    value,
    expiresAt: Date.now() + ttlMs,
  });
};

const invalidateByPrefix = (prefix: string) => {
  runtimeCache.forEach((_, key) => {
    if (key.startsWith(prefix)) {
      runtimeCache.delete(key);
    }
  });
};

const PRODUCTS_CACHE_TTL_MS = 45_000;
const CATEGORIES_CACHE_TTL_MS = 120_000;
const GENERAL_CONFIG_CACHE_TTL_MS = 20_000;

const withCompanyHeader = (companyId?: string) =>
  companyId ? { headers: { 'X-Company-Id': companyId } } : undefined;

// Header Config API
export const getHeaderConfig = async (companyId?: string) => {
  try {
    const response = await ecommerceClient.get(
      `${API_BASE_URL}/admin/header`,
      withCompanyHeader(companyId)
    );
    return response.data;
  } catch (error) {
    console.error('Header config getirme hatası:', error);
    throw error;
  }
};

export const saveHeaderConfig = async (data: any) => {
  try {
    const response = await ecommerceClient.post(
      `${API_BASE_URL}/admin/header/save`,
      data,
      withCompanyHeader(data?.companyId)
    );
    return response.data;
  } catch (error) {
    console.error('Header config kaydetme hatası:', error);
    throw error;
  }
};

// Menu Config API
export const getMenuConfig = async (companyId?: string) => {
  try {
    const response = await ecommerceClient.get(
      `${API_BASE_URL}/admin/menu`,
      withCompanyHeader(companyId)
    );
    return response.data;
  } catch (error) {
    console.error('Menu config getirme hatası:', error);
    throw error;
  }
};

export const saveMenuConfig = async (data: any) => {
  try {
    const response = await ecommerceClient.post(
      `${API_BASE_URL}/admin/menu/save`,
      data,
      withCompanyHeader(data?.companyId)
    );
    return response.data;
  } catch (error) {
    console.error('Menu config kaydetme hatası:', error);
    throw error;
  }
};

// E-ticaret kategorileri - hiyerarşi bilgisiyle (parentId, level) - CategoriesController
// X-Company-Id header otomatik olarak apiClient interceptor'ı tarafından eklenir
export const getPublicCategoriesWithHierarchy = async (_companyId?: string) => {
  try {
    const cacheKey = `publicCategories:${_companyId || 'default'}`;
    const cached = getCached<any[]>(cacheKey);
    if (cached) return cached;

    const response = await ecommerceClient.get(`${API_BASE_URL}/categories`);
    const data = response.data?.data || response.data;
    const normalized = Array.isArray(data) ? data : [];
    setCached(cacheKey, normalized, CATEGORIES_CACHE_TTL_MS);
    return normalized;
  } catch (error) {
    console.error('Hiyerarşik kategoriler getirme hatası:', error);
    return [];
  }
};

// Menu Categories API
export const getMenuCategories = async (_companyId: string, configId: string) => {
  try {
    const response = await ecommerceClient.get(
      `${API_BASE_URL}/admin/menu/categories/${configId}`,
      withCompanyHeader(_companyId)
    );
    return response.data;
  } catch (error) {
    console.error('Menu kategorileri getirme hatası:', error);
    throw error;
  }
};

export const saveMenuCategory = async (data: any) => {
  try {
    const response = await ecommerceClient.post(
      `${API_BASE_URL}/admin/menu/categories/save`,
      data,
      withCompanyHeader(data?.companyId)
    );
    return response.data;
  } catch (error) {
    console.error('Menu kategorisi kaydetme hatası:', error);
    throw error;
  }
};

export const reorderMenuCategories = async (data: any) => {
  try {
    const response = await ecommerceClient.post(
      `${API_BASE_URL}/admin/menu/categories/reorder`,
      data,
      withCompanyHeader(data?.companyId)
    );
    return response.data;
  } catch (error) {
    console.error('Menu kategorileri sıralama hatası:', error);
    throw error;
  }
};

// General Config API (Favicon)
export const getGeneralConfig = async (_companyId?: string): Promise<{ success?: boolean; data?: GeneralConfigData }> => {
  try {
    const cacheKey = `generalConfig:${_companyId || 'default'}`;
    const cached = getCached<any>(cacheKey);
    if (cached) return cached;

    const response = await ecommerceClient.get(
      `${API_BASE_URL}/admin/config`,
      withCompanyHeader(_companyId)
    );
    setCached(cacheKey, response.data, GENERAL_CONFIG_CACHE_TTL_MS);
    return response.data;
  } catch (error) {
    console.error('General config getirme hatası:', error);
    throw error;
  }
};

export const saveGeneralConfig = async (data: GeneralConfigData) => {
  try {
    const response = await ecommerceClient.post(
      `${API_BASE_URL}/admin/config/save`,
      data,
      withCompanyHeader(data.companyId)
    );
    invalidateByPrefix('generalConfig:');
    return response.data;
  } catch (error) {
    console.error('General config kaydetme hatası:', error);
    throw error;
  }
};

// ===== B2B Ayarları (Faz 5.1) =====
export interface B2BSettingsData {
  configId?: string | null;
  storeMode?: string;
  hidePricesWhenLoggedOut?: boolean;
  allowB2CRegistration?: boolean;
  b2bPricesVatExcluded?: boolean;
  storeCurrency?: string;
  exchangeRateMode?: string;
  manualExchangeRate?: number | null;
  showExchangeRateInHeader?: boolean;
  /** true + TRY dışı mağaza: bireysel (B2C) müşteri fiyatları TL görür ve TL öder */
  b2cPricesInTry?: boolean;
  onlineCollectionBankAccountId?: number | null;
}

export const getB2BSettings = async (companyId?: string): Promise<{ success?: boolean; data?: B2BSettingsData }> => {
  try {
    const response = await ecommerceClient.get(
      `${API_BASE_URL}/admin/config/b2b`,
      withCompanyHeader(companyId)
    );
    return response.data;
  } catch (error) {
    console.error('B2B ayarları getirme hatası:', error);
    throw error;
  }
};

export const saveB2BSettings = async (data: B2BSettingsData, companyId?: string) => {
  try {
    const response = await ecommerceClient.post(
      `${API_BASE_URL}/admin/config/b2b/save`,
      data,
      withCompanyHeader(companyId)
    );
    invalidateByPrefix('generalConfig:');
    return response.data;
  } catch (error) {
    console.error('B2B ayarları kaydetme hatası:', error);
    throw error;
  }
};

// Site Background API
export const getSiteBackgrounds = async (_companyId?: string) => {
  try {
    const response = await ecommerceClient.get(`${API_BASE_URL}/admin/backgrounds`);
    return response.data;
  } catch (error) {
    console.error('Site backgrounds getirme hatası:', error);
    throw error;
  }
};

export const getActiveSiteBackground = async (_companyId?: string) => {
  try {
    const response = await ecommerceClient.get(`${API_BASE_URL}/admin/backgrounds/active`);
    return response.data;
  } catch (error) {
    console.error('Aktif site background getirme hatası:', error);
    throw error;
  }
};

export const saveSiteBackground = async (data: any) => {
  try {
    const response = await ecommerceClient.post(`${API_BASE_URL}/admin/backgrounds/save`, data);
    return response.data;
  } catch (error) {
    console.error('Site background kaydetme hatası:', error);
    throw error;
  }
};

// Product Modules API
export const getProductModules = async (_companyId: string, moduleType?: string) => {
  try {
    const url = moduleType 
      ? `${API_BASE_URL}/product-modules?moduleType=${moduleType}`
      : `${API_BASE_URL}/product-modules`;
    const response = await ecommerceClient.get(url);
    return response.data;
  } catch (error) {
    console.error('Product modules getirme hatası:', error);
    throw error;
  }
};

export const saveProductModule = async (data: any) => {
  try {
    const response = await ecommerceClient.post(`${API_BASE_URL}/product-modules/save`, data);
    return response.data;
  } catch (error) {
    console.error('Product module kaydetme hatası:', error);
    throw error;
  }
};

export const deleteProductModule = async (data: any) => {
  try {
    const response = await ecommerceClient.post(`${API_BASE_URL}/product-modules/delete`, data);
    return response.data;
  } catch (error) {
    console.error('Product module silme hatası:', error);
    throw error;
  }
};

// ✅ YENİ: E-ticarete açık ürünleri getir (SQL'den IsEcommerce = 1)
export const getEcommerceProducts = async (companyId: string) => {
  try {
    const cacheKey = `ecommerceProducts:${companyId || 'default'}`;
    const cached = getCached<any[]>(cacheKey);
    if (cached) return cached;

    console.log('🛒 [ECOMMERCE PRODUCTS] SQL\'den e-ticarete açık ürünler çekiliyor:', companyId);
    const response = await accountingApi.get(`${INVENTORY_API_BASE_URL}/items/ecommerce`);
    console.log('✅ [ECOMMERCE PRODUCTS] Başarılı:', response.data.length, 'ürün');
    const products = Array.isArray(response.data) ? response.data : [];
    setCached(cacheKey, products, PRODUCTS_CACHE_TTL_MS);
    return products;
  } catch (error) {
    console.error('❌ [ECOMMERCE PRODUCTS] E-ticaret ürünleri getirme hatası:', error);
    throw error;
  }
};

// ✅ YENİ: Belirli ID'lere sahip ürünlerin detaylarını getir
export const getProductsByIds = async (companyId: string, productIds: string[]) => {
  try {
    console.log('🔍 [PRODUCT DETAILS] Ürün detayları çekiliyor:', { companyId, count: productIds.length });
    const allProducts = await getEcommerceProducts(companyId);
    const idSet = new Set((productIds || []).map((id) => String(id)));
    
    // Sadece istenen ID'lere sahip ürünleri filtrele
    const filteredProducts = (Array.isArray(allProducts) ? allProducts : []).filter((product: any) => 
      idSet.has(String(product?.id))
    );
    
    console.log('✅ [PRODUCT DETAILS] Bulunan ürün sayısı:', filteredProducts.length);
    return filteredProducts;
  } catch (error) {
    console.error('❌ [PRODUCT DETAILS] Ürün detayları getirme hatası:', error);
    throw error;
  }
};

// ✅ YENİ: Belirli ID'lere sahip kategorilerin detaylarını getir
export const getCategoriesByIds = async (companyId: string, _configId: string, categoryIds: string[]) => {
  try {
    console.log('🔍 [CATEGORY DETAILS] Kategori detayları çekiliyor:', { companyId, count: categoryIds.length });
    const categories = await getPublicCategoriesWithHierarchy(companyId);
    const filteredCategories = (Array.isArray(categories) ? categories : [])
      .filter((category: any) => categoryIds.includes(String(category?.id || '')))
      .map((category: any) => ({
        categoryId: String(category?.id || ''),
        categoryName: String(category?.name || ''),
        categoryImageUrl: category?.imageUrl || null,
      }));

    console.log('✅ [CATEGORY DETAILS] Bulunan kategori sayısı:', filteredCategories.length);
    return filteredCategories;
  } catch (error) {
    console.error('❌ [CATEGORY DETAILS] Kategori detayları getirme hatası:', error);
    throw error;
  }
};

// ========================================
// CATEGORY SETTINGS API
// ========================================

/**
 * Kategori ayarlarını API yanıtından normalize eder
 */
export const normalizeCategorySettingsFromApi = (data: Record<string, unknown> | null | undefined) => {
  if (!data) return null;

  let titleStyles = data.titleStyles as Record<string, unknown> | undefined;
  if (!titleStyles && typeof data.titleStylesJson === 'string') {
    try {
      titleStyles = JSON.parse(data.titleStylesJson);
    } catch {
      titleStyles = undefined;
    }
  }

  const rawShowOnHomepage =
    data.showOnHomepage ??
    data.showCategoriesOnHomepage ??
    data.ShowCategoriesOnHomepage;

  const displayStyleRaw = data.displayStyle as Record<string, unknown> | string | undefined;
  const webLayout =
    (typeof displayStyleRaw === 'object' && displayStyleRaw
      ? displayStyleRaw.web ?? displayStyleRaw.Web
      : displayStyleRaw) ??
    data.homepageCategoryLayout ??
    data.HomepageCategoryLayout ??
    'grid';
  const mobileLayout =
    (typeof displayStyleRaw === 'object' && displayStyleRaw
      ? displayStyleRaw.mobile ?? displayStyleRaw.Mobile
      : undefined) ??
    data.mobileCategoryLayout ??
    data.MobileCategoryLayout ??
    'list';

  const menuDropdownStyleRaw = String(
    data.menuDropdownStyle ?? data.MenuDropdownStyle ?? 'minimal'
  ).toLowerCase();
  const menuDropdownStyle =
    menuDropdownStyleRaw === 'list'
      ? 'list' as const
      : menuDropdownStyleRaw === 'classic'
        ? 'classic' as const
        : 'minimal' as const;

  return {
    showOnHomepage: rawShowOnHomepage === undefined ? true : Boolean(rawShowOnHomepage),
    displayStyle: {
      web: String(webLayout),
      mobile: String(mobileLayout),
    },
    itemsPerRow: Number(data.itemsPerRow ?? data.categoriesPerRow ?? data.CategoriesPerRow ?? 4),
    titleStyles: titleStyles || {
      fontSize: 16,
      color: '#ffffff',
      bold: true,
      italic: false,
      fontFamily: 'Inter',
      strokeWidth: 0,
      strokeColor: '#000000',
    },
    title: String(data.title ?? data.homepageCategoryTitle ?? data.HomepageCategoryTitle ?? 'Kategoriler'),
    menuDropdownStyle,
    menuDropdownColumns: Number(data.menuDropdownColumns ?? data.MenuDropdownColumns ?? 4),
    menuDropdownMaxHeight: Number(data.menuDropdownMaxHeight ?? data.MenuDropdownMaxHeight ?? 400),
  };
};

export const getCategorySettings = async (_companyId: string, configId: string) => {
  try {
    console.log('📂 [CATEGORY] SQL\'den kategori ayarları çekiliyor:', { configId });
    const response = await ecommerceClient.get(`${ECOMMERCE_API_URL}/api/ecommerce/admin/category-settings/${configId}`);
    console.log('✅ [CATEGORY] Kategori ayarları başarıyla getirildi');
    return response.data;
  } catch (error) {
    console.error('❌ [CATEGORY] Kategori ayarları getirme hatası:', error);
    throw error;
  }
};

/**
 * Kategori ayarlarını kaydeder
 */
export const saveCategorySettings = async (data: any) => {
  try {
    const url = `${ECOMMERCE_API_URL}/api/ecommerce/admin/category-settings/save`;
    console.log('💾 [CATEGORY] SQL\'e kategori ayarları kaydediliyor:', data);
    console.log('🌐 [CATEGORY] API URL:', url);
    const response = await ecommerceClient.post(url, data);
    console.log('✅ [CATEGORY] Kategori ayarları başarıyla kaydedildi');
    console.log('📥 [CATEGORY] Backend yanıtı:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('❌ [CATEGORY] Kategori ayarları kaydetme hatası:', error);
    console.error('❌ [CATEGORY] Hata detayı:', {
      message: error?.message,
      status: error?.response?.status,
      data: error?.response?.data
    });
    throw error;
  }
};

// ========================================
// SLIDER API
// ========================================

export const getSliderSettings = async (_companyId: string, configId: string) => {
  try {
    console.log('🎠 [SLIDER] SQL\'den slider ayarları çekiliyor:', configId);
    const response = await ecommerceClient.get(`${API_BASE_URL}/admin/slider/${configId}`);
    console.log('✅ [SLIDER] Slider ayarları başarıyla getirildi');
    return response.data;
  } catch (error) {
    console.error('❌ [SLIDER] Slider ayarları getirme hatası:', error);
    throw error;
  }
};

export const saveSliderSettings = async (data: any) => {
  try {
    console.log('💾 [SLIDER] SQL\'e slider ayarları kaydediliyor:', data);
    const response = await ecommerceClient.post(`${API_BASE_URL}/admin/slider/save-settings`, data);
    console.log('✅ [SLIDER] Slider ayarları başarıyla kaydedildi');
    return response.data;
  } catch (error) {
    console.error('❌ [SLIDER] Slider ayarları kaydetme hatası:', error);
    throw error;
  }
};

export const addSlide = async (data: any) => {
  try {
    console.log('➕ [SLIDER] Yeni slide ekleniyor:', data);
    const response = await ecommerceClient.post(`${API_BASE_URL}/admin/slider/add-slide`, data);
    console.log('✅ [SLIDER] Slide başarıyla eklendi');
    return response.data;
  } catch (error) {
    console.error('❌ [SLIDER] Slide ekleme hatası:', error);
    throw error;
  }
};

export const updateSlide = async (data: any) => {
  try {
    console.log('✏️ [SLIDER] Slide güncelleniyor:', data);
    const response = await ecommerceClient.post(`${API_BASE_URL}/admin/slider/update-slide`, data);
    console.log('✅ [SLIDER] Slide başarıyla güncellendi');
    return response.data;
  } catch (error) {
    console.error('❌ [SLIDER] Slide güncelleme hatası:', error);
    throw error;
  }
};

export const toggleSlide = async (data: any) => {
  try {
    console.log('🔄 [SLIDER] Slide durumu değiştiriliyor:', data);
    const response = await ecommerceClient.post(`${API_BASE_URL}/admin/slider/toggle-slide`, data);
    console.log('✅ [SLIDER] Slide durumu başarıyla değiştirildi');
    return response.data;
  } catch (error) {
    console.error('❌ [SLIDER] Slide durumu değiştirme hatası:', error);
    throw error;
  }
};

// ✅ YENİ: Product Settings API
export const getProductSettings = async (_companyId: string, configId: string) => {
  try {
    console.log('🛍️ [PRODUCT SETTINGS] SQL den ayarlar çekiliyor:', configId);
    const response = await ecommerceClient.get(`${API_BASE_URL}/admin/product-settings/${configId}`);
    console.log('✅ [PRODUCT SETTINGS] Ayarlar başarıyla getirildi');
    return response.data;
  } catch (error) {
    console.error('❌ [PRODUCT SETTINGS] Ayarlar getirme hatası:', error);
    throw error;
  }
};

export const saveProductSettings = async (data: any) => {
  try {
    console.log('💾 [PRODUCT SETTINGS] SQL e ayarlar kaydediliyor:', data);
    const response = await ecommerceClient.post(`${API_BASE_URL}/admin/product-settings/save`, data);
    console.log('✅ [PRODUCT SETTINGS] Ayarlar başarıyla kaydedildi');
    return response.data;
  } catch (error) {
    console.error('❌ [PRODUCT SETTINGS] Ayarlar kaydetme hatası:', error);
    throw error;
  }
};

// ✅ YENİ: Product Detail Settings API
export const getProductDetailSettings = async (_companyId: string, configId: string) => {
  try {
    console.log('📄 [PRODUCT DETAIL SETTINGS] SQL den ayarlar çekiliyor:', configId);
    const response = await ecommerceClient.get(`${API_BASE_URL}/admin/product-detail-settings/${configId}`);
    console.log('✅ [PRODUCT DETAIL SETTINGS] Ayarlar başarıyla getirildi');
    return response.data;
  } catch (error) {
    console.error('❌ [PRODUCT DETAIL SETTINGS] Ayarlar getirme hatası:', error);
    throw error;
  }
};

export const saveProductDetailSettings = async (data: any) => {
  try {
    console.log('💾 [PRODUCT DETAIL SETTINGS] SQL e ayarlar kaydediliyor:', data);
    const response = await ecommerceClient.post(`${API_BASE_URL}/admin/product-detail-settings/save`, data);
    console.log('✅ [PRODUCT DETAIL SETTINGS] Ayarlar başarıyla kaydedildi');
    return response.data;
  } catch (error) {
    console.error('❌ [PRODUCT DETAIL SETTINGS] Ayarlar kaydetme hatası:', error);
    throw error;
  }
};

// ✅ YENİ: Footer Settings API
export const getFooterSettings = async (_companyId: string, configId: string) => {
  try {
    console.log('🦶 [FOOTER SETTINGS] SQL den ayarlar çekiliyor:', configId);
    const response = await ecommerceClient.get(`${API_BASE_URL}/admin/footer/${configId}`);
    console.log('✅ [FOOTER SETTINGS] Ayarlar başarıyla getirildi');
    return response.data;
  } catch (error) {
    console.error('❌ [FOOTER SETTINGS] Ayarlar getirme hatası:', error);
    throw error;
  }
};

export const saveFooterSettings = async (data: any) => {
  try {
    console.log('💾 [FOOTER SETTINGS] SQL e ayarlar kaydediliyor:', data);
    const response = await ecommerceClient.post(`${API_BASE_URL}/admin/footer/save`, data);
    console.log('✅ [FOOTER SETTINGS] Ayarlar başarıyla kaydedildi');
    return response.data;
  } catch (error) {
    console.error('❌ [FOOTER SETTINGS] Ayarlar kaydetme hatası:', error);
    throw error;
  }
};

// ✅ YENİ: Cart Settings API
export const getCartSettings = async (_companyId: string, configId: string) => {
  try {
    console.log('🛒 [CART SETTINGS] SQL den ayarlar çekiliyor:', { configId });
    const response = await ecommerceClient.get(`${API_BASE_URL}/admin/cart-settings/${configId}`);
    console.log('✅ [CART SETTINGS] Ayarlar başarıyla getirildi');
    return response.data;
  } catch (error) {
    console.error('❌ [CART SETTINGS] Ayarlar getirme hatası:', error);
    throw error;
  }
};

export const saveCartSettings = async (data: any) => {
  try {
    console.log('💾 [CART SETTINGS] SQL e ayarlar kaydediliyor:', data);
    const response = await ecommerceClient.post(`${API_BASE_URL}/admin/cart-settings/save`, data);
    console.log('✅ [CART SETTINGS] Ayarlar başarıyla kaydedildi');
    return response.data;
  } catch (error) {
    console.error('❌ [CART SETTINGS] Ayarlar kaydetme hatası:', error);
    throw error;
  }
};

export const getLoginPageSettings = async (companyId: string, configId: string) => {
  try {
    const response = await ecommerceClient.get(
      `${API_BASE_URL}/admin/login-page-settings/${configId}`,
      withCompanyHeader(companyId)
    );
    return response.data;
  } catch (error) {
    console.error('❌ [LOGIN PAGE SETTINGS] Ayarlar getirme hatası:', error);
    throw error;
  }
};

export const saveLoginPageSettings = async (data: any) => {
  try {
    const response = await ecommerceClient.post(
      `${API_BASE_URL}/admin/login-page-settings/save`,
      data,
      withCompanyHeader(data?.companyId)
    );
    return response.data;
  } catch (error) {
    console.error('❌ [LOGIN PAGE SETTINGS] Ayarlar kaydetme hatası:', error);
    throw error;
  }
};

// ✅ YENİ: About Settings API
export const getAboutSettings = async (_companyId: string, configId: string) => {
  try {
    console.log('📖 [ABOUT SETTINGS] SQL den ayarlar çekiliyor:', { configId });
    const response = await ecommerceClient.get(`${API_BASE_URL}/admin/about-settings/${configId}`);
    console.log('✅ [ABOUT SETTINGS] Ayarlar başarıyla getirildi');
    return response.data;
  } catch (error) {
    console.error('❌ [ABOUT SETTINGS] Ayarlar getirme hatası:', error);
    throw error;
  }
};

export const saveAboutSettings = async (data: any) => {
  try {
    console.log('💾 [ABOUT SETTINGS] SQL e ayarlar kaydediliyor:', data);
    const response = await ecommerceClient.post(`${API_BASE_URL}/admin/about-settings/save`, data);
    console.log('✅ [ABOUT SETTINGS] Ayarlar başarıyla kaydedildi');
    return response.data;
  } catch (error) {
    console.error('❌ [ABOUT SETTINGS] Ayarlar kaydetme hatası:', error);
    throw error;
  }
};

// ✅ YENİ: About Tabs (Dynamic Pages) API
export const getAboutTabs = async (configId: string, pageType: string = 'AboutTab') => {
  try {
    console.log(`📖 [${pageType.toUpperCase()}] SQL den dinamik sekmeler çekiliyor:`, configId);
    const url = `${API_BASE_URL}/admin/about-tabs/${configId}?pageType=${pageType}`;
    const response = await ecommerceClient.get(url);
    console.log(`✅ [${pageType.toUpperCase()}] Sekmeler başarıyla getirildi:`, response.data.data?.length || 0, 'sekme');
    return response.data;
  } catch (error) {
    console.error(`❌ [${pageType.toUpperCase()}] Sekmeler getirme hatası:`, error);
    throw error;
  }
};

export const saveAboutTab = async (data: any) => {
  try {
    console.log('💾 [ABOUT TABS] SQL e sekme kaydediliyor:', data);
    const response = await ecommerceClient.post(`${API_BASE_URL}/admin/about-tabs/save`, data);
    console.log('✅ [ABOUT TABS] Sekme başarıyla kaydedildi');
    return response.data;
  } catch (error) {
    console.error('❌ [ABOUT TABS] Sekme kaydetme hatası:', error);
    throw error;
  }
};

export const deleteAboutTab = async (tabId: string, companyId: string) => {
  try {
    console.log('🗑️ [ABOUT TABS] SQL den sekme siliniyor:', tabId, companyId);
    // 🔒 MULTI-TENANT: companyId eklendi
    const response = await ecommerceClient.post(`${API_BASE_URL}/admin/about-tabs/delete`, { 
      id: tabId,
      companyId: companyId
    });
    console.log('✅ [ABOUT TABS] Sekme başarıyla silindi');
    return response.data;
  } catch (error) {
    console.error('❌ [ABOUT TABS] Sekme silme hatası:', error);
    throw error;
  }
};

// ========================================
// CUSTOM PAGES API (Kullanıcı Tanımlı Sayfalar)
// ========================================

export interface CustomPageData {
  id?: string;
  configId: string;
  title: string;
  subtitle?: string;
  content?: string;
  slug?: string;
  metaTitle?: string;
  metaDescription?: string;
  isActive?: boolean;
  showInMenu?: boolean;
  showInFooter?: boolean;
  displayOrder?: number;
  createdAt?: string;
  updatedAt?: string;
}

export const getCustomPages = async (configId: string) => {
  try {
    console.log('📖 [CUSTOM PAGES] Sayfalar çekiliyor:', configId);
    const response = await ecommerceClient.get(`${API_BASE_URL}/admin/custom-pages/${configId}`);
    console.log('✅ [CUSTOM PAGES] Sayfalar getirildi:', response.data.data?.length || 0);
    return response.data;
  } catch (error) {
    console.error('❌ [CUSTOM PAGES] Sayfa listesi hatası:', error);
    throw error;
  }
};

export const saveCustomPage = async (data: CustomPageData) => {
  try {
    console.log('💾 [CUSTOM PAGES] Sayfa kaydediliyor:', data?.title);
    const response = await ecommerceClient.post(`${API_BASE_URL}/admin/custom-pages/save`, data);
    console.log('✅ [CUSTOM PAGES] Sayfa kaydedildi:', response.data?.data?.id);
    return response.data;
  } catch (error) {
    console.error('❌ [CUSTOM PAGES] Sayfa kaydetme hatası:', error);
    throw error;
  }
};

export const deleteCustomPage = async (pageId: string) => {
  try {
    console.log('🗑️ [CUSTOM PAGES] Sayfa siliniyor:', pageId);
    const response = await ecommerceClient.post(`${API_BASE_URL}/admin/custom-pages/delete`, { id: pageId });
    console.log('✅ [CUSTOM PAGES] Sayfa silindi');
    return response.data;
  } catch (error) {
    console.error('❌ [CUSTOM PAGES] Sayfa silme hatası:', error);
    throw error;
  }
};

// ========================================
// CUSTOM FORMS API (Kullanıcı Tanımlı Formlar)
// ========================================

export type CustomFormFieldType =
  | 'text'
  | 'textarea'
  | 'email'
  | 'phone'
  | 'number'
  | 'select'
  | 'radio'
  | 'checkbox'
  | 'date'
  | 'city_district';

export interface CustomFormField {
  id: string;
  type: CustomFormFieldType;
  label: string;
  placeholder?: string;
  helpText?: string;
  required: boolean;
  options?: string[];
}

export interface CustomFormData {
  id?: string;
  configId: string;
  title: string;
  slug?: string;
  description?: string;
  submitButtonText?: string;
  successMessage?: string;
  notifyEmail?: string;
  fieldsJson?: string;
  isActive?: boolean;
  displayOrder?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface CustomFormSubmission {
  id: string;
  formId: string;
  companyId: string;
  dataJson?: string | null;
  submitterIp?: string | null;
  userAgent?: string | null;
  pageSlug?: string | null;
  isRead: boolean;
  createdAt: string;
  updatedAt?: string | null;
}

export const getCustomForms = async (configId: string) => {
  try {
    console.log('📖 [CUSTOM FORMS] Formlar çekiliyor:', configId);
    const response = await ecommerceClient.get(`${API_BASE_URL}/admin/forms/${configId}`);
    console.log('✅ [CUSTOM FORMS] Formlar getirildi:', response.data.data?.length || 0);
    return response.data;
  } catch (error) {
    console.error('❌ [CUSTOM FORMS] Form listesi hatası:', error);
    throw error;
  }
};

export const saveCustomForm = async (data: CustomFormData) => {
  try {
    console.log('💾 [CUSTOM FORMS] Form kaydediliyor:', data?.title);
    const response = await ecommerceClient.post(`${API_BASE_URL}/admin/forms/save`, data);
    console.log('✅ [CUSTOM FORMS] Form kaydedildi:', response.data?.data?.id);
    return response.data;
  } catch (error) {
    console.error('❌ [CUSTOM FORMS] Form kaydetme hatası:', error);
    throw error;
  }
};

export const deleteCustomForm = async (formId: string) => {
  try {
    console.log('🗑️ [CUSTOM FORMS] Form siliniyor:', formId);
    const response = await ecommerceClient.post(`${API_BASE_URL}/admin/forms/delete`, { id: formId });
    console.log('✅ [CUSTOM FORMS] Form silindi');
    return response.data;
  } catch (error) {
    console.error('❌ [CUSTOM FORMS] Form silme hatası:', error);
    throw error;
  }
};

export const getCustomFormSubmissions = async (
  formId: string,
  page: number = 1,
  pageSize: number = 25,
  isRead?: boolean
) => {
  try {
    const params: Record<string, string | number> = { page, pageSize };
    if (typeof isRead === 'boolean') params.isRead = String(isRead);
    const response = await ecommerceClient.get(
      `${API_BASE_URL}/admin/forms/${formId}/submissions`,
      { params }
    );
    return response.data;
  } catch (error) {
    console.error('❌ [CUSTOM FORMS] Gönderim listesi hatası:', error);
    throw error;
  }
};

export const markCustomFormSubmissionRead = async (id: string, isRead: boolean = true) => {
  try {
    const response = await ecommerceClient.post(
      `${API_BASE_URL}/admin/forms/submissions/mark-read`,
      { id, isRead }
    );
    return response.data;
  } catch (error) {
    console.error('❌ [CUSTOM FORMS] İşaretleme hatası:', error);
    throw error;
  }
};

export const deleteCustomFormSubmission = async (id: string) => {
  try {
    const response = await ecommerceClient.post(
      `${API_BASE_URL}/admin/forms/submissions/delete`,
      { id }
    );
    return response.data;
  } catch (error) {
    console.error('❌ [CUSTOM FORMS] Gönderim silme hatası:', error);
    throw error;
  }
};

// ========================================
// SERVICE SETTINGS API (Hizmetler Sabit Tab)
// ========================================

export const getServiceSettings = async (_companyId: string, configId: string) => {
  try {
    console.log('📖 [SERVICE SETTINGS] SQL den ayarlar çekiliyor:', configId);
    const response = await ecommerceClient.get(`${API_BASE_URL}/admin/service-settings/${configId}`);
    console.log('✅ [SERVICE SETTINGS] Ayarlar başarıyla çekildi');
    return response.data;
  } catch (error) {
    console.error('❌ [SERVICE SETTINGS] Ayarlar çekme hatası:', error);
    throw error;
  }
};

export const saveServiceSettings = async (data: any) => {
  try {
    console.log('💾 [SERVICE SETTINGS] SQL e ayarlar kaydediliyor:', data);
    const response = await ecommerceClient.post(`${API_BASE_URL}/admin/service-settings/save`, data);
    console.log('✅ [SERVICE SETTINGS] Ayarlar başarıyla kaydedildi');
    return response.data;
  } catch (error) {
    console.error('❌ [SERVICE SETTINGS] Ayarlar kaydetme hatası:', error);
    throw error;
  }
};

// ========================================
// PRODUCT SOLUTION SETTINGS API
// ========================================

/**
 * CompanyId'ye göre Product Solution ayarlarını getirir
 */
/**
 * ConfigId'ye göre Product Solution ayarlarını getirir
 */
export const getProductSolutionSettings = async (_companyId: string, configId: string) => {
  try {
    console.log('📖 [PRODUCT SOLUTION] SQL den ayarlar çekiliyor (ConfigId):', configId);
    const response = await ecommerceClient.get(`${API_BASE_URL}/admin/product-solution-settings/${configId}`);
    console.log('✅ [PRODUCT SOLUTION] Ayarlar başarıyla çekildi');
    return response.data;
  } catch (error) {
    console.error('❌ [PRODUCT SOLUTION] Ayarlar çekme hatası:', error);
    throw error;
  }
};

/**
 * Product Solution ayarlarını kaydeder (Create veya Update)
 */
export const saveProductSolutionSettings = async (data: any) => {
  try {
    console.log('💾 [PRODUCT SOLUTION] SQL e ayarlar kaydediliyor:', data);
    const response = await ecommerceClient.post(`${API_BASE_URL}/admin/product-solution-settings/save`, data);
    console.log('✅ [PRODUCT SOLUTION] Ayarlar başarıyla kaydedildi');
    return response.data;
  } catch (error) {
    console.error('❌ [PRODUCT SOLUTION] Ayarlar kaydetme hatası:', error);
    throw error;
  }
};

// ============================================
// 📞 CONTACT SETTINGS API
// ============================================

/**
 * ConfigId ile İletişim Ayarlarını Getir
 */
export const getContactSettings = async (_companyId: string, configId: string) => {
  try {
    console.log('📖 [CONTACT] SQL den ayarlar çekiliyor:', { configId });
    const response = await ecommerceClient.get(`${API_BASE_URL}/admin/contact-settings/${configId}`);
    console.log('✅ [CONTACT] Ayarlar başarıyla çekildi');
    return response.data;
  } catch (error) {
    console.error('❌ [CONTACT] Ayarlar çekme hatası:', error);
    throw error;
  }
};

/**
 * İletişim Ayarlarını Kaydet
 */
export const saveContactSettings = async (data: any) => {
  try {
    console.log('💾 [CONTACT] SQL e ayarlar kaydediliyor:', data);
    const response = await ecommerceClient.post(`${API_BASE_URL}/admin/contact-settings/save`, data);
    console.log('✅ [CONTACT] Ayarlar başarıyla kaydedildi');
    return response.data;
  } catch (error) {
    console.error('❌ [CONTACT] Ayarlar kaydetme hatası:', error);
    throw error;
  }
};

// ============================================
// 🍽️ RESTAURANT SETTINGS API
// ============================================

export const getRestaurantSettings = async (_companyId: string, configId: string) => {
  try {
    console.log('📖 [RESTAURANT] SQL den ayarlar çekiliyor:', { configId });
    const response = await ecommerceClient.get(`${API_BASE_URL}/admin/restaurants-settings/${configId}`);
    console.log('✅ [RESTAURANT] Ayarlar başarıyla çekildi');
    return response.data;
  } catch (error) {
    console.error('❌ [RESTAURANT] Ayarlar çekme hatası:', error);
    throw error;
  }
};

export const saveRestaurantSettings = async (data: {
  configId: string;
  heroEnabled: boolean;
  heroTitle: string;
  heroDescription: string;
  heroBackgroundColor: string;
}) => {
  try {
    console.log('💾 [RESTAURANT] SQL e branch page ayarları kaydediliyor:', data);
    const response = await ecommerceClient.post(`${API_BASE_URL}/admin/restaurants-settings/save`, data);
    console.log('✅ [RESTAURANT] Branch page ayarları kaydedildi');
    return response.data;
  } catch (error) {
    console.error('❌ [RESTAURANT] Branch page ayarları kaydetme hatası:', error);
    throw error;
  }
};

/**
 * Restoran sayfasındaki şubelerin e-ticaret görünüm alanlarını (ImageUrl, MapUrl,
 * DisplayOrder, ShowOnEcommerce) günceller. Şube listesi mevcut Branches tablosundan
 * gelir; bu endpoint yalnızca o şubedeki ek alanları günceller.
 *
 * @param branchId Branch.FirebaseId veya numeric Branch.Id
 */
export const updateRestaurantBranch = async (
  branchId: string,
  data: {
    imageUrl?: string | null;
    mapUrl?: string | null;
    displayOrder?: number;
    showOnEcommerce?: boolean;
  },
) => {
  try {
    const response = await ecommerceClient.post(
      `${API_BASE_URL}/admin/restaurants-settings/branch/${encodeURIComponent(branchId)}/update`,
      data,
    );
    return response.data;
  } catch (error) {
    console.error('❌ [RESTAURANT BRANCH] Güncelleme hatası:', error);
    throw error;
  }
};

/**
 * Referans Ayarlarını Getir (ConfigId ile)
 */
export const getReferenceSettings = async (_companyId: string, configId: string) => {
  try {
    console.log('📖 [REFERENCE] SQL den ayarlar çekiliyor (ConfigId):', configId);
    const response = await ecommerceClient.get(`${API_BASE_URL}/admin/reference-settings/${configId}`);
    console.log('✅ [REFERENCE] Ayarlar başarıyla çekildi');
    return response.data;
  } catch (error) {
    console.error('❌ [REFERENCE] Ayarlar çekme hatası:', error);
    throw error;
  }
};

/**
 * Referans Ayarlarını Kaydet
 */
export const saveReferenceSettings = async (data: any) => {
  try {
    console.log('💾 [REFERENCE] SQL e ayarlar kaydediliyor:', data);
    const response = await ecommerceClient.post(`${API_BASE_URL}/admin/reference-settings/save`, data);
    console.log('✅ [REFERENCE] Ayarlar başarıyla kaydedildi');
    return response.data;
  } catch (error) {
    console.error('❌ [REFERENCE] Ayarlar kaydetme hatası:', error);
    throw error;
  }
};

/**
 * Yeni Referans Firması Ekle
 */
export const addReferenceItem = async (data: any) => {
  try {
    console.log('➕ [REFERENCE ITEM] Yeni referans ekleniyor:', data);
    const response = await ecommerceClient.post(`${API_BASE_URL}/admin/reference-settings/item`, data);
    console.log('✅ [REFERENCE ITEM] Referans başarıyla eklendi');
    return response.data;
  } catch (error) {
    console.error('❌ [REFERENCE ITEM] Referans ekleme hatası:', error);
    throw error;
  }
};

/**
 * Referans Firmasını Güncelle
 */
export const updateReferenceItem = async (id: string, data: any) => {
  try {
    console.log('🔄 [REFERENCE ITEM] Referans güncelleniyor:', id, data);
    const response = await ecommerceClient.post(`${API_BASE_URL}/admin/reference-settings/item/${id}/update`, data); // ✅ POST ve /update eklendi
    console.log('✅ [REFERENCE ITEM] Referans başarıyla güncellendi');
    return response.data;
  } catch (error) {
    console.error('❌ [REFERENCE ITEM] Referans güncelleme hatası:', error);
    throw error;
  }
};

/**
 * Referans Firmasını Sil
 */
export const deleteReferenceItem = async (id: string, companyId: string) => { // ✅ companyId eklendi
  try {
    console.log('🗑️ [REFERENCE ITEM] Referans siliniyor:', id);
    const response = await ecommerceClient.post(`${API_BASE_URL}/admin/reference-settings/item/${id}/delete`, { companyId }); // ✅ POST ve companyId eklendi
    console.log('✅ [REFERENCE ITEM] Referans başarıyla silindi');
    return response.data;
  } catch (error) {
    console.error('❌ [REFERENCE ITEM] Referans silme hatası:', error);
    throw error;
  }
};

// ========================================
// FAQ SETTINGS API
// ========================================

export const getFAQSettings = async (_companyId: string, configId: string) => {
  try {
    console.log('📖 [FAQ] SQL den ayarlar çekiliyor:', { configId });
    const response = await ecommerceClient.get(`${API_BASE_URL}/admin/faq-settings/${configId}`);
    console.log('✅ [FAQ] Ayarlar başarıyla çekildi');
    return response.data;
  } catch (error) {
    console.error('❌ [FAQ] Ayarlar çekme hatası:', error);
    throw error;
  }
};

export const saveFAQSettings = async (data: any) => {
  try {
    console.log('💾 [FAQ] SQL e ayarlar kaydediliyor:', data);
    const response = await ecommerceClient.post(`${API_BASE_URL}/admin/faq-settings/save`, data);
    console.log('✅ [FAQ] Ayarlar başarıyla kaydedildi');
    return response.data;
  } catch (error) {
    console.error('❌ [FAQ] Ayarlar kaydetme hatası:', error);
    throw error;
  }
};

export const saveFAQCategory = async (data: any) => {
  try {
    console.log('💾 [FAQ CATEGORY] SQL e kategori kaydediliyor:', data);
    const response = await ecommerceClient.post(`${API_BASE_URL}/admin/faq-settings/category`, data);
    console.log('✅ [FAQ CATEGORY] Kategori başarıyla kaydedildi');
    return response.data;
  } catch (error) {
    console.error('❌ [FAQ CATEGORY] Kategori kaydetme hatası:', error);
    throw error;
  }
};

export const deleteFAQCategory = async (id: string, companyId: string) => { // ✅ companyId eklendi
  try {
    console.log('🗑️ [FAQ CATEGORY] SQL den kategori siliniyor:', { id, companyId });
    const response = await ecommerceClient.post(`${API_BASE_URL}/admin/faq-settings/category/delete`, { id, companyId }); // ✅ POST ve body kullanımı
    console.log('✅ [FAQ CATEGORY] Kategori başarıyla silindi');
    return response.data;
  } catch (error) {
    console.error('❌ [FAQ CATEGORY] Kategori silme hatası:', error);
    throw error;
  }
};

export const saveFAQItem = async (data: any) => {
  try {
    console.log('💾 [FAQ ITEM] SQL e soru kaydediliyor:', data);
    const response = await ecommerceClient.post(`${API_BASE_URL}/admin/faq-settings/item`, data);
    console.log('✅ [FAQ ITEM] Soru başarıyla kaydedildi');
    return response.data;
  } catch (error) {
    console.error('❌ [FAQ ITEM] Soru kaydetme hatası:', error);
    throw error;
  }
};

export const deleteFAQItem = async (id: string, companyId: string) => { // ✅ companyId eklendi
  try {
    console.log('🗑️ [FAQ ITEM] SQL den soru siliniyor:', { id, companyId });
    const response = await ecommerceClient.post(`${API_BASE_URL}/admin/faq-settings/item/delete`, { id, companyId }); // ✅ POST ve body kullanımı
    console.log('✅ [FAQ ITEM] Soru başarıyla silindi');
    return response.data;
  } catch (error) {
    console.error('❌ [FAQ ITEM] Soru silme hatası:', error);
    throw error;
  }
};

// ==================== KAMPANYA (E-Ticaret Pazarlama) ====================

export interface CampaignData {
  id?: string;
  companyId?: string;
  name: string;
  description?: string;
  type: string; // birincil tür (ProductDiscount | Coupon | ...)
  /** Çoklu kampanya türleri (Trendyol tarzı birden fazla mekanik) */
  types?: string[];
  startDate: string;
  endDate: string;
  discountType: string; // Percentage | Amount
  discountValue: number;
  maxDiscountAmount?: number | null;
  minCartAmount?: number | null;
  buyQuantity?: number | null;
  payQuantity?: number | null;
  couponCode?: string | null;
  usageLimit?: number | null;
  usageLimitPerCustomer?: number | null;
  usedCount?: number;
  scopeType: string; // All | Products | Categories | Brands
  priority?: number;
  /** Özel gün / sezon anahtarı (mothers-day, black-friday, ...) */
  specialDay?: string | null;
  isActive: boolean;
  status?: string; // passive | upcoming | active | expired (backend türetir)
  productIds?: string[];
  categoryIds?: string[];
  brandIds?: string[];
  createdAt?: string;
  updatedAt?: string;
}

export const getEcommerceBrands = async (companyId?: string) => {
  const response = await ecommerceClient.get(
    `${API_BASE_URL}/brands`,
    withCompanyHeader(companyId)
  );
  const payload = response?.data?.data ?? response?.data;
  return Array.isArray(payload) ? payload : [];
};

const CAMPAIGNS_BASE_URL = `${API_BASE_URL}/admin/campaigns`;

export const getCampaigns = async (companyId?: string, status?: string, type?: string) => {
  const params = new URLSearchParams();
  if (status) params.append('status', status);
  if (type) params.append('type', type);
  const query = params.toString() ? `?${params.toString()}` : '';
  const response = await ecommerceClient.get(`${CAMPAIGNS_BASE_URL}${query}`, withCompanyHeader(companyId));
  return response.data;
};

export const getCampaign = async (id: string, companyId?: string) => {
  const response = await ecommerceClient.get(`${CAMPAIGNS_BASE_URL}/${id}`, withCompanyHeader(companyId));
  return response.data;
};

export const createCampaign = async (data: CampaignData) => {
  const response = await ecommerceClient.post(CAMPAIGNS_BASE_URL, data, withCompanyHeader(data.companyId));
  return response.data;
};

export const updateCampaign = async (id: string, data: CampaignData) => {
  // Production: PUT yasak — POST /update/{id}
  const response = await ecommerceClient.post(`${CAMPAIGNS_BASE_URL}/update/${id}`, data, withCompanyHeader(data.companyId));
  return response.data;
};

export const toggleCampaign = async (id: string, isActive: boolean, companyId?: string) => {
  const response = await ecommerceClient.post(`${CAMPAIGNS_BASE_URL}/${id}/toggle`, { isActive }, withCompanyHeader(companyId));
  return response.data;
};

export const deleteCampaign = async (id: string, companyId?: string) => {
  // Production: DELETE yasak — POST /delete/{id}
  const response = await ecommerceClient.post(`${CAMPAIGNS_BASE_URL}/delete/${id}`, {}, withCompanyHeader(companyId));
  return response.data;
};

export const getCampaignStats = async (id: string, companyId?: string) => {
  const response = await ecommerceClient.get(`${CAMPAIGNS_BASE_URL}/${id}/stats`, withCompanyHeader(companyId));
  return response.data;
};

export default {
  getHeaderConfig,
  saveHeaderConfig,
  getMenuConfig,
  saveMenuConfig,
  getMenuCategories,
  saveMenuCategory,
  reorderMenuCategories,
  getGeneralConfig,
  saveGeneralConfig,
  getSiteBackgrounds,
  getActiveSiteBackground,
  saveSiteBackground,
  getProductModules,
  saveProductModule,
  deleteProductModule,
  getEcommerceProducts, // ✅ YENİ
  getProductsByIds, // ✅ YENİ
  getCategoriesByIds, // ✅ YENİ
  getSliderSettings, // ✅ SLIDER
  saveSliderSettings, // ✅ SLIDER
  addSlide, // ✅ SLIDER
  updateSlide, // ✅ SLIDER
  toggleSlide, // ✅ SLIDER
  getCategorySettings, // ✅ CATEGORY SETTINGS
  saveCategorySettings, // ✅ CATEGORY SETTINGS
  getProductSettings, // ✅ PRODUCT SETTINGS
  saveProductSettings, // ✅ PRODUCT SETTINGS
  getProductDetailSettings, // ✅ PRODUCT DETAIL SETTINGS
  saveProductDetailSettings, // ✅ PRODUCT DETAIL SETTINGS
  getFooterSettings, // ✅ FOOTER SETTINGS
  getContactSettings, // ✅ CONTACT SETTINGS
  saveContactSettings, // ✅ CONTACT SETTINGS
  getRestaurantSettings, // ✅ RESTAURANT SETTINGS
  saveRestaurantSettings, // ✅ RESTAURANT SETTINGS
  updateRestaurantBranch, // ✅ RESTAURANT BRANCHES (Branches tablosundan)
  saveFooterSettings, // ✅ FOOTER SETTINGS
  getCartSettings, // ✅ CART SETTINGS
  saveCartSettings, // ✅ CART SETTINGS
  getLoginPageSettings, // ✅ LOGIN PAGE SETTINGS
  saveLoginPageSettings, // ✅ LOGIN PAGE SETTINGS
  getAboutSettings, // ✅ ABOUT SETTINGS
  saveAboutSettings, // ✅ ABOUT SETTINGS
  getAboutTabs, // ✅ ABOUT TABS (Dynamic Pages)
  saveAboutTab, // ✅ ABOUT TABS (Dynamic Pages)
  deleteAboutTab, // ✅ ABOUT TABS (Dynamic Pages)
  getCustomPages, // ✅ CUSTOM PAGES (Kullanıcı tanımlı sayfalar)
  saveCustomPage, // ✅ CUSTOM PAGES
  deleteCustomPage, // ✅ CUSTOM PAGES
  getCustomForms, // ✅ CUSTOM FORMS (Kullanıcı tanımlı formlar)
  saveCustomForm, // ✅ CUSTOM FORMS
  deleteCustomForm, // ✅ CUSTOM FORMS
  getCustomFormSubmissions, // ✅ CUSTOM FORMS - Submissions
  markCustomFormSubmissionRead, // ✅ CUSTOM FORMS - Submissions
  deleteCustomFormSubmission, // ✅ CUSTOM FORMS - Submissions
  getCampaigns, // ✅ KAMPANYA
  getCampaign, // ✅ KAMPANYA
  createCampaign, // ✅ KAMPANYA
  updateCampaign, // ✅ KAMPANYA
  toggleCampaign, // ✅ KAMPANYA
  deleteCampaign, // ✅ KAMPANYA
  getCampaignStats, // ✅ KAMPANYA
};

